import { Clock } from 'lucide-react';
import { useMemo } from 'react';
import _ from 'lodash';

import { notifySuccess } from '@/portainer/services/notifications';
import { useEdgeGroups } from '@/react/edge/edge-groups/queries/useEdgeGroups';
import { useList } from '@/react/portainer/environments/update-schedules/queries/list';
import { DecoratedItem } from '@/react/portainer/environments/update-schedules/ListView/types';
import {
  EdgeUpdateSchedule,
  StatusType,
} from '@/react/portainer/environments/update-schedules/types';
import { useRemoveMutation } from '@/react/portainer/environments/update-schedules/queries/useRemoveMutation';

import { Datatable } from '@@/datatables';
import { AddButton } from '@@/buttons';
import { useTableState } from '@@/datatables/useTableState';
import { DeleteButton } from '@@/buttons/DeleteButton';

import { createStore } from './datatable-store';
import { columns } from './columns';

const storageKey = 'update-schedules-list';
const settingsStore = createStore(storageKey);

export function UpdateSchedulesDatatable() {
  const tableState = useTableState(settingsStore, storageKey);

  const listQuery = useList({ includeEdgeStacks: true });
  const groupsQuery = useEdgeGroups({
    select: (groups) => Object.fromEntries(groups.map((g) => [g.Id, g.Name])),
  });

  const items: Array<DecoratedItem> = useMemo(() => {
    if (!listQuery.data || !groupsQuery.data) {
      return [];
    }

    return listQuery.data.map((item) => ({
      ...item,
      edgeGroupNames: _.compact(
        item.edgeGroupIds.map((id) => groupsQuery.data[id])
      ),
    }));
  }, [listQuery.data, groupsQuery.data]);

  if (!listQuery.data || !groupsQuery.data) {
    return null;
  }

  return (
    <Datatable
      dataset={items}
      columns={columns}
      settingsManager={tableState}
      title="Update & rollback"
      titleIcon={Clock}
      isLoading={listQuery.isLoading}
      renderTableActions={(selectedRows) => (
        <TableActions selectedRows={selectedRows} />
      )}
      isRowSelectable={(row) => row.original.status !== StatusType.Sent}
      data-cy="environment-update-schedules-datatable"
    />
  );
}

function TableActions({
  selectedRows,
}: {
  selectedRows: EdgeUpdateSchedule[];
}) {
  const removeMutation = useRemoveMutation();
  return (
    <>
      <DeleteButton
        onConfirmed={() => handleRemove()}
        disabled={selectedRows.length === 0}
        data-cy="remove-update-schedules-button"
        confirmMessage="Are you sure you want to remove these schedules?"
      />
      <AddButton to=".new" data-cy="add-update-schedules-button">
        Add update & rollback schedule
      </AddButton>
    </>
  );

  async function handleRemove() {
    removeMutation.mutate(selectedRows, {
      onSuccess: () => {
        notifySuccess('Success', 'Schedules successfully removed');
      },
    });
  }
}
